import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatBytes, formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UploadCloud, History } from "lucide-react";

const SOURCE_LABEL = {
  free: "Complimentary",
  one_time: "Single credit",
  subscription: "Subscription",
};

export default function UploadsHistory() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/uploads").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  return (
    <DashboardLayout
      title="Upload History"
      subtitle="Every audio file you've submitted for analysis."
      action={
        <Button asChild className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
          <Link to="/upload" data-testid="uploads-new-btn"><UploadCloud className="h-4 w-4 mr-2" />New Analysis</Link>
        </Button>
      }
    >
      <div className="rounded-xl border border-slate-800 bg-[#0A1124] overflow-hidden">
        {rows && rows.length === 0 ? (
          <EmptyState />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">File</TableHead>
                <TableHead className="text-slate-400">Size</TableHead>
                <TableHead className="text-slate-400">Access</TableHead>
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400 text-right">Report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rows || []).map((r) => (
                <TableRow key={r.upload_id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`upload-row-${r.upload_id}`}>
                  <TableCell className="font-medium text-white">{r.filename}</TableCell>
                  <TableCell className="font-mono text-slate-400">{formatBytes(r.size_bytes)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-slate-700 text-slate-300">
                      {SOURCE_LABEL[r.access_source] || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/reports/${r.report_id}`} className="text-cyan-400 hover:underline text-sm" data-testid={`upload-report-link-${r.upload_id}`}>
                      View
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </DashboardLayout>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-900">
        <History className="h-5 w-5 text-slate-500" />
      </div>
      <p className="text-slate-400">No uploads yet.</p>
      <Button asChild variant="outline" className="border-slate-700 text-white hover:bg-slate-800 hover:text-white">
        <Link to="/upload" data-testid="uploads-empty-cta">Upload your first file</Link>
      </Button>
    </div>
  );
}
